import React, { useState, useEffect, useCallback } from 'react';
import {
    MessageSquare, Send, ArrowDownRight, ShieldAlert, Key,
    AlertTriangle, Loader2, BarChart2, Zap, Clock,
    Lock, Cloud, Cpu,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import { SectionHeader, Section, StatusBadge, EmptyMetric } from './OverviewTabV2';

/**
 * LessonChatTab — "Is Lesson Chat useful, reliable, and cost-justified?"
 *
 * Derives metrics from workspace_events filtered for lesson_chat_* events.
 * Renders "Partial telemetry" honestly for metrics not yet derivable.
 */

const CHAT_EVENTS = [
    'lesson_chat_opened',
    'lesson_chat_prompt_sent',
    'lesson_chat_response_received',
    'lesson_chat_auth_failure',
    'lesson_chat_quota_hit',
    'lesson_chat_feature_disabled',
    'lesson_chat_provider_failure',
];

const INTENT_COLORS = {
    differentiation: '#6366f1',
    assessment: '#10b981',
    engagement: '#f59e0b',
    revision: '#3b82f6',
    clarification: '#8b5cf6',
    general: '#64748b',
};

const LessonChatTab = ({ filters = {} }) => {
    const [chatEvents, setChatEvents] = useState(null);
    const [_featureUsage, setFeatureUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const days = filters.days || 7;
            // Fetch chat-specific events and feature usage in parallel
            const [eventsRes, featureRes] = await Promise.allSettled([
                adminAnalytics.getEvents({
                    days,
                    event_name: 'lesson_chat',  // prefix-match via backend
                    page_size: 200,
                }),
                adminAnalytics.getFeatureUsage({ days }),
            ]);

            if (eventsRes.status === 'fulfilled') setChatEvents(eventsRes.value.data);
            if (featureRes.status === 'fulfilled') setFeatureUsage(featureRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load Lesson Chat data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading Lesson Chat metrics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    // Derive event counts from the events list
    const events = chatEvents?.events || [];
    const eventCounts = {};
    events.forEach(ev => {
        const name = ev.event_name || '';
        eventCounts[name] = (eventCounts[name] || 0) + 1;
    });

    const panelOpens = eventCounts['lesson_chat_opened'] || 0;
    const promptsSent = eventCounts['lesson_chat_prompt_sent'] || 0;
    const responsesReceived = eventCounts['lesson_chat_response_received'] || 0;
    const authFailures = eventCounts['lesson_chat_auth_failure'] || 0;
    const quotaHits = eventCounts['lesson_chat_quota_hit'] || 0;
    const providerFailures = eventCounts['lesson_chat_provider_failure'] || 0;

    const totalEvents = events.length;
    const hasData = totalEvents > 0;

    // Multi-turn rate: sessions with >1 prompt
    const sessionPrompts = {};
    events.filter(e => e.event_name === 'lesson_chat_prompt_sent').forEach(e => {
        const sid = e.session_id || 'unknown';
        sessionPrompts[sid] = (sessionPrompts[sid] || 0) + 1;
    });
    const multiTurnSessions = Object.values(sessionPrompts).filter(c => c > 1).length;
    const totalSessions = Object.keys(sessionPrompts).length;
    const multiTurnRate = totalSessions > 0 ? Math.round((multiTurnSessions / totalSessions) * 100) : null;

    // Avg messages per lesson (rough: prompts / opens)
    const avgMessagesPerLesson = panelOpens > 0 ? (promptsSent / panelOpens).toFixed(1) : null;

    // Failure rates
    const authFailureRate = promptsSent > 0 ? ((authFailures / promptsSent) * 100).toFixed(1) : null;
    const quotaHitRate = promptsSent > 0 ? ((quotaHits / promptsSent) * 100).toFixed(1) : null;
    const providerFailureRate = promptsSent > 0 ? ((providerFailures / promptsSent) * 100).toFixed(1) : null;

    return (
        <div>
            <SectionHeader
                title="Lesson Chat"
                subtitle="Is Lesson Chat useful, reliable, and cost-justified?"
            />

            {/* Beta status context */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: 10,
                background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)',
                marginBottom: 16, fontSize: 12, color: '#4338ca',
            }}>
                <MessageSquare size={13} />
                <span><strong>Hidden Internal Beta</strong> — Lesson Chat is available only to allowlisted users. Metrics will grow as adoption expands.</span>
            </div>

            {!hasData && (
                <div style={{
                    padding: '16px 20px', borderRadius: 12,
                    background: '#fffbeb', border: '1px solid #fde68a',
                    marginBottom: 24, fontSize: 13, color: '#92400e',
                    display: 'flex', alignItems: 'center', gap: 8,
                }}>
                    <AlertTriangle size={14} />
                    No Lesson Chat events found in this period. This is expected during early beta — metrics will populate as teachers use Lesson Chat.
                </div>
            )}

            {/* ── Engagement ──────────────────────────────────────── */}
            <Section title="Engagement" icon={MessageSquare} color="#6366f1">
                <MetricCard label="Panel Opens" value={panelOpens} icon={MessageSquare} accent="#6366f1"
                    sub={hasData ? `${totalEvents} total events` : 'Awaiting usage'} />
                <MetricCard label="Prompts Sent" value={promptsSent} icon={Send} accent="#10b981"
                    sub={panelOpens > 0 && promptsSent === 0 ? 'Users opened but did not prompt' : null} />
                <MetricCard label="Responses Received" value={responsesReceived} icon={ArrowDownRight} accent="#3b82f6"
                    sub={promptsSent > 0 && responsesReceived === 0 ? 'No responses yet — check provider' : null} />
                {multiTurnRate != null ? (
                    <MetricCard label="Multi-Turn Rate" value={multiTurnRate} icon={BarChart2} accent="#8b5cf6" format="percent"
                        sub={`${multiTurnSessions} of ${totalSessions} sessions had >1 prompt`} />
                ) : (
                    <EmptyMetric label="Multi-Turn Rate" reason={hasData ? 'partial' : 'no_data'} />
                )}
                {avgMessagesPerLesson != null ? (
                    <MetricCard label="Avg Messages / Lesson" value={avgMessagesPerLesson} icon={MessageSquare} accent="#f59e0b"
                        sub={`${promptsSent} prompts across ${panelOpens} opens`} />
                ) : (
                    <EmptyMetric label="Avg Messages / Lesson" reason={hasData ? 'partial' : 'no_data'} />
                )}
            </Section>

            {/* ── Reliability ─────────────────────────────────────── */}
            <Section title="Reliability" icon={ShieldAlert} color="#ef4444">
                {authFailureRate != null ? (
                    <MetricCard label="Auth Failure Rate" value={authFailureRate} icon={Key} accent={parseFloat(authFailureRate) > 5 ? '#ef4444' : '#10b981'} format="percent" />
                ) : (
                    <EmptyMetric label="Auth Failure Rate" reason={hasData ? 'partial' : 'no_data'} />
                )}
                {quotaHitRate != null ? (
                    <MetricCard label="Quota Hit Rate" value={quotaHitRate} icon={Lock} accent={parseFloat(quotaHitRate) > 5 ? '#ef4444' : '#10b981'} format="percent" />
                ) : (
                    <EmptyMetric label="Quota Hit Rate" reason={hasData ? 'partial' : 'no_data'} />
                )}
                {providerFailureRate != null ? (
                    <MetricCard label="Provider Failure Rate" value={providerFailureRate} icon={Cloud} accent={parseFloat(providerFailureRate) > 5 ? '#ef4444' : '#10b981'} format="percent" />
                ) : (
                    <EmptyMetric label="Provider Failure Rate" reason={hasData ? 'partial' : 'no_data'} />
                )}
            </Section>

            {/* ── Cost / Token Metrics ────────────────────────────── */}
            <Section title="Cost & Tokens" icon={Cpu} color="#f59e0b">
                <EmptyMetric label="Avg Tokens / Turn" reason="not_instrumented" />
                <EmptyMetric label="High-Token Outlier Rate" reason="not_instrumented" />
                <div style={{
                    gridColumn: '1 / -1', fontSize: 11, color: '#94a3b8',
                    padding: '4px 0', fontStyle: 'italic',
                }}>
                    Token metrics require backend instrumentation of token counts per LLM call. Not yet wired.
                </div>
            </Section>

            {/* ── Intent Distribution ─────────────────────────────── */}
            <IntentDistribution events={events} />

            {/* ── Raw Event Counts ────────────────────────────────── */}
            <div style={{ marginTop: 24 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 12, paddingBottom: 8,
                    borderBottom: '2px solid #94a3b820',
                }}>
                    <Zap size={15} style={{ color: '#94a3b8' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Raw Event Counts</h3>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {totalEvents} total events
                    </span>
                </div>
                <div style={{
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                    overflow: 'hidden',
                }}>
                    {CHAT_EVENTS.map((eName, i) => (
                        <div key={eName} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 16px',
                            borderBottom: i < CHAT_EVENTS.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}>
                            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>{eName}</span>
                            <span style={{
                                fontSize: 13, fontWeight: 700,
                                color: (eventCounts[eName] || 0) > 0 ? '#111827' : '#d1d5db',
                            }}>
                                {eventCounts[eName] || 0}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};


// ─── Intent Distribution ────────────────────────────────────────────────────

const IntentDistribution = ({ events }) => {
    // Check if any events have intent metadata
    const intents = {};
    events.forEach(ev => {
        const intent = ev.event_data?.intent || ev.intent;
        if (intent) {
            intents[intent] = (intents[intent] || 0) + 1;
        }
    });

    const hasIntents = Object.keys(intents).length > 0;

    return (
        <div style={{ marginTop: 28 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 8,
                borderBottom: '2px solid #6366f120',
            }}>
                <BarChart2 size={15} style={{ color: '#6366f1' }} />
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Intent Distribution</h3>
            </div>

            {!hasIntents ? (
                <div style={{
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: 14, padding: '24px', textAlign: 'center',
                }}>
                    <StatusBadge status="partial" />
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
                        Intent classification requires chat prompts to carry intent metadata.
                        This will populate automatically when the backend intent classifier is wired to telemetry.
                    </p>
                </div>
            ) : (
                <div style={{
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 14, padding: '20px 24px',
                }}>
                    {Object.entries(intents).sort((a, b) => b[1] - a[1]).map(([intent, count], idx) => {
                        const total = Object.values(intents).reduce((s, c) => s + c, 0);
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        const color = INTENT_COLORS[intent] || '#94a3b8';
                        return (
                            <div key={intent} style={{ marginBottom: idx < Object.keys(intents).length - 1 ? 10 : 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{intent}</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color }}>{count} ({pct}%)</span>
                                </div>
                                <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', width: `${pct}%`, background: color,
                                        borderRadius: 3, transition: 'width 0.3s',
                                    }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


export default LessonChatTab;
